import React, { useEffect, useState, useRef } from 'react';
import { MoveHorizontal } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import { Point, FaceAnalysis } from './analyzeFace';
interface BeforeAfterSliderProps {
  imageSrc: string;
  landmarks?: Point[];
  analysis?: FaceAnalysis;
  /** 0-1: filter intensity multiplier for the "after" image */
  intensity?: number;
}
// Helper: take normalized landmark and flip X for mirrored display
const flip = (p: Point) => ({
  x: 1 - p.x,
  y: p.y
});
const avg = (pts: Point[]): Point => ({
  x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
  y: pts.reduce((s, p) => s + p.y, 0) / pts.length
});
export function BeforeAfterSlider({
  imageSrc,
  landmarks = [],
  analysis,
  intensity = 1
}: BeforeAfterSliderProps) {
  // Map intensity 0-1 → CSS filter values
  const contrast = 100 + 5 * intensity;
  const brightness = 100 + 5 * intensity;
  const saturate = 100 + 5 * intensity;
  const afterFilter = `contrast(${contrast}%) brightness(${brightness}%) saturate(${saturate}%)`;
  const {
    t
  } = useLanguage();
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = x / rect.width * 100;
    setSliderPosition(percent);
  };
  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };
  const onTouchMove = (e: TouchEvent) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };
  const stopDragging = () => setIsDragging(false);
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', stopDragging);
      window.addEventListener('touchmove', onTouchMove, {
        passive: false
      });
      window.addEventListener('touchend', stopDragging);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stopDragging);
    };
  }, [isDragging]);
  // Build marker list — personalized if we have landmarks, otherwise fixed fallback
  const hasLandmarks = landmarks.length === 68;
  const recommendedIds = new Set((analysis?.recommendations ?? []).map(r => r.id));
  type Marker = {
    x: number;
    y: number;
    size: 'lg' | 'md' | 'sm';
    color: string;
    key: string;
  };
  const markers: Marker[] = [];
  if (hasLandmarks) {
    // Cheeks — midpoint between cheek edge (2,14) and nose corner (31,35)
    if (recommendedIds.has('cheeks')) {
      const leftCheek = flip(avg([landmarks[2], landmarks[31]]));
      const rightCheek = flip(avg([landmarks[14], landmarks[35]]));
      markers.push({
        x: leftCheek.x,
        y: leftCheek.y,
        size: 'lg',
        color: 'bg-brand-copper shadow-[0_0_12px_#c79268]',
        key: 'cheek-l'
      });
      markers.push({
        x: rightCheek.x,
        y: rightCheek.y,
        size: 'lg',
        color: 'bg-brand-copper shadow-[0_0_12px_#c79268]',
        key: 'cheek-r'
      });
    }
    // Lips — center of mouth (points 48-67)
    if (recommendedIds.has('lips')) {
      const mouth = flip(avg(landmarks.slice(48, 68)));
      markers.push({
        x: mouth.x,
        y: mouth.y,
        size: 'md',
        color: 'bg-pink-400 shadow-[0_0_10px_#f472b6]',
        key: 'lips'
      });
    }
    // Nasolabial — midpoint between nose corner and mouth corner
    if (recommendedIds.has('nasolabial')) {
      const nlLeft = flip(avg([landmarks[31], landmarks[48]]));
      const nlRight = flip(avg([landmarks[35], landmarks[54]]));
      markers.push({
        x: nlLeft.x,
        y: nlLeft.y,
        size: 'sm',
        color: 'bg-brand-lightCopper shadow-[0_0_8px_#e6b89c]',
        key: 'nl-l'
      });
      markers.push({
        x: nlRight.x,
        y: nlRight.y,
        size: 'sm',
        color: 'bg-brand-lightCopper shadow-[0_0_8px_#e6b89c]',
        key: 'nl-r'
      });
    }
    // Jawline — points 5 and 11
    if (recommendedIds.has('jawline')) {
      const jawL = flip(landmarks[5]);
      const jawR = flip(landmarks[11]);
      markers.push({
        x: jawL.x,
        y: jawL.y,
        size: 'sm',
        color: 'bg-amber-300 shadow-[0_0_8px_#fcd34d]',
        key: 'jaw-l'
      });
      markers.push({
        x: jawR.x,
        y: jawR.y,
        size: 'sm',
        color: 'bg-amber-300 shadow-[0_0_8px_#fcd34d]',
        key: 'jaw-r'
      });
    }
  } else {
    // Fallback fixed positions (no detected landmarks)
    if (!analysis || recommendedIds.has('cheeks')) {
      markers.push({
        x: 0.3,
        y: 0.45,
        size: 'lg',
        color: 'bg-brand-copper shadow-[0_0_10px_#c79268]',
        key: 'cheek-l'
      });
      markers.push({
        x: 0.7,
        y: 0.45,
        size: 'lg',
        color: 'bg-brand-copper shadow-[0_0_10px_#c79268]',
        key: 'cheek-r'
      });
    }
    if (!analysis || recommendedIds.has('nasolabial')) {
      markers.push({
        x: 0.4,
        y: 0.6,
        size: 'sm',
        color: 'bg-brand-lightCopper shadow-[0_0_8px_#e6b89c]',
        key: 'nl-l'
      });
      markers.push({
        x: 0.6,
        y: 0.6,
        size: 'sm',
        color: 'bg-brand-lightCopper shadow-[0_0_8px_#e6b89c]',
        key: 'nl-r'
      });
    }
    if (!analysis || recommendedIds.has('lips')) {
      markers.push({
        x: 0.5,
        y: 0.7,
        size: 'md',
        color: 'bg-pink-400 shadow-[0_0_8px_#f472b6]',
        key: 'lips'
      });
    }
  }
  const sizeClass = (s: 'lg' | 'md' | 'sm') => s === 'lg' ? 'w-3 h-3' : s === 'md' ? 'w-2.5 h-2.5' : 'w-2 h-2';
  return <div ref={containerRef} className="relative w-full max-w-md mx-auto aspect-[3/4] sm:aspect-[4/5] bg-gray-100 rounded-2xl overflow-hidden select-none cursor-ew-resize shadow-xl" onMouseDown={e => {
    setIsDragging(true);
    handleMove(e.clientX);
  }} onTouchStart={e => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  }} data-id="element-204">
      {/* BEFORE IMAGE (Bottom Layer) */}
      <div className="absolute inset-0 w-full h-full" data-id="element-205">
        <img src={imageSrc} alt="Before" className="w-full h-full object-cover" style={{
        transform: 'scaleX(-1)'
      }} data-id="element-206" />
        <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium tracking-wider" data-id="element-207">
          {t('faceAnalysis.results.before')}
        </div>
      </div>

      {/* AFTER IMAGE (Top Layer, Clipped) */}
      <div className="absolute inset-0 w-full h-full border-r-2 border-white" style={{
      clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)`
    }} data-id="element-208">
        <img src={imageSrc} alt="After" className="w-full h-full object-cover" style={{
        transform: 'scaleX(-1)',
        filter: afterFilter
      }} data-id="element-209" />

        {/* Personalized treatment markers */}
        <div className="absolute inset-0 pointer-events-none" style={{
        opacity: Math.max(0.2, intensity)
      }} data-id="element-210">
          {markers.map(m => <div key={m.key} className={`absolute ${sizeClass(m.size)} ${m.color} rounded-full animate-pulse -translate-x-1/2 -translate-y-1/2`} style={{
          left: `${m.x * 100}%`,
          top: `${m.y * 100}%`
        }} data-id="element-211" />)}
        </div>

        <div className="absolute top-4 right-4 bg-brand-copper/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium tracking-wider" data-id="element-212">
          {t('faceAnalysis.results.after')}
        </div>
      </div>

      {/* SLIDER HANDLE */}
      <div className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10" style={{
      left: `${sliderPosition}%`,
      transform: 'translateX(-50%)'
    }} data-id="element-213">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-brand-copper" data-id="element-214">
          <MoveHorizontal size={16} data-id="element-215" />
        </div>
      </div>
    </div>;
}